import { clamp } from '@iconik-io/core/src/clamp';
import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import type { PlaylistItem } from '../../types';
import { getPreferredPlaylistItemVariant } from '../../utils/get_preferred_playlist_item_variant';
import { throttle } from '../../utils/throttle';
import { CompareAwarePlayer } from '../compare_aware_player/compare_aware_player';
import { HiddenMediaRelease } from './hidden_media_release';
import { MediaEventHandler } from './media_event_handler';
import { MediaPlayerAudioNodes } from './processors/audio_nodes';
import { SubtitleHandler } from './subtitle_handler';

const logger = DebugLogger.getInstance('unified_player_core');

export class MediaPlayer extends CompareAwarePlayer {
  declare mediaElement: HTMLMediaElement | undefined;

  eventHandler: MediaEventHandler | undefined;

  reverseInterval: ReturnType<typeof setInterval> | undefined; // Reference to the looping interval handling reverse playback

  #subtitleHandler: SubtitleHandler | undefined;

  #audioNodes: MediaPlayerAudioNodes | undefined;

  #hiddenRelease: HiddenMediaRelease | undefined;

  async init(): Promise<void> {
    await super.init();

    if (!this.isComparePlayer) {
      this.refreshStoreCurrentTime();
    }

    if (this.mediaElement && !this.isComparePlayer) {
      this.#subtitleHandler = new SubtitleHandler(
        this.mediaElement,
        this.containerElement,
        this.subscribersList,
        this.store,
      );
    }

    this.subscribersList.subscribe(
      this.store.playableMedia.$currentPlaylistItemVariantUrl,
      this.replaceSource,
    );

    this.subscribersList.listen(
      this.store.playableMedia.$shouldBePlaying,
      this.#handleShouldBePlaying,
    );
    this.subscribersList.listen(
      this.store.playableMedia.$targetTime,
      this.seekToTargetTime,
    );
    this.subscribersList.subscribe(
      this.store.playableMedia.$playbackRate,
      this.handlePlaybackRate,
    );
    this.subscribersList.listen(
      this.store.playableMedia.$playableRange,
      this.handlePlayableRangeTime,
    );
    this.subscribersList.subscribe(
      this.store.playableMedia.$currentTime,
      this.handleCurrentTime,
    );
    if (this.#subtitleHandler) {
      this.subscribersList.subscribe(
        this.store.playableMedia.$currentSubtitles,
        this.#subtitleHandler.setupSubtitles,
      );
    }

    if (!this.mediaElement) {
      return;
    }

    if (this.playerType !== 'stream') {
      this.#hiddenRelease = new HiddenMediaRelease(this.mediaElement, {
        isPlaying: () => this.store.playableMedia.$shouldBePlaying.get(),
      });
      this.#hiddenRelease.start();
    }

    if (this.isComparePlayer) {
      this.mediaElement.muted = true;
      return;
    }

    this.eventHandler = new MediaEventHandler(
      this.mediaElement,
      this.store,
      this.#handleShouldBePlaying,
    );
    this.eventHandler.init();
  }

  async destroy() {
    this.#hiddenRelease?.destroy();
    this.mediaElement?.removeEventListener(
      'loadedmetadata',
      this.#handleAudioNodesMetadata,
    );
    await super.destroy();
    this.eventHandler?.destroy();
    this.#subtitleHandler?.destroy();
    this.#audioNodes?.destroy();
    if (!this.isComparePlayer) {
      this.store.playableMedia.setPlaying(false);
    }
  }

  #handleShouldBePlaying = (shouldBePlaying: boolean) => {
    this.reloadIfMediaError();
    const segmentRange = this.store.segment.$currentSegment.get().range;
    const isSingleFrame =
      segmentRange?.start !== undefined &&
      segmentRange.start === segmentRange.end;

    const lastPlayableFrameTime = Math.min(
      segmentRange?.end ?? Infinity,
      this.store.playableMedia.$lastPlayableFrameTime.get(),
      this.store.playableMedia.$lastFoundFrameTime.get() ?? Infinity,
    );
    const playingForward = this.store.playableMedia.$playbackRate.get() > 0;
    if (shouldBePlaying) {
      if (lastPlayableFrameTime <= (this.mediaElement?.currentTime ?? 0)) {
        if (isSingleFrame) {
          // if the segment is only a single frame, dismiss active segment and play on
          this.store.segment.clearCurrentSegment();
        } else if (playingForward) {
          this.store.handleLooping();
        }
      }
      this.play();
    } else {
      this.clearReverseInterval();
      this.pause();
      this.#hiddenRelease?.schedule();
    }
  };

  seekToTargetTime = (time: number | undefined) => {
    if (time === undefined) {
      return;
    }
    if (!this.isMediaElementInitialized()) {
      return;
    }
    this.#hiddenRelease?.restore();
    this.reloadIfMediaError();
    const currentItem = this.store.playlist.$currentPlaylistItem.get();
    if (!currentItem) {
      return;
    }
    const clampedTime = this.store.clampTime(time);

    const adjustedTime =
      clampedTime + this.store.playlist.$frameDuration.get() / 3;
    this.mediaElement.currentTime = adjustedTime;
  };

  handlePlaybackRate = (playbackRate: number) => {
    if (!this.isMediaElementInitialized()) {
      return;
    }
    this.#hiddenRelease?.restore();
    this.clearReverseInterval();
    if (playbackRate >= 0) {
      // Don't set playback rate on the media element for negative rate
      this.mediaElement.playbackRate = playbackRate;
    }
    this.#handleShouldBePlaying(
      this.store.playableMedia.$shouldBePlaying.get(),
    );
  };

  clearReverseInterval = () => {
    clearInterval(this.reverseInterval);
    this.reverseInterval = undefined;
  };

  /**
   * Reverses the playback of the media element.
   */
  playInReverse = () => {
    this.pause();
    const currentPlaylistItem = this.store.playlist.$currentPlaylistItem.get();
    const fps = clamp(
      currentPlaylistItem?.contentType === 'video'
        ? (currentPlaylistItem?.frameRate ?? 60)
        : 60,
      1,
      120,
    );

    // Calculate the frame step based on fps and playback rate
    const frameStep =
      (1000 / fps / 1000) * -this.store.playableMedia.$playbackRate.get();

    // Set an interval to move the media element backwards frame by frame
    if (this.reverseInterval) {
      return;
    }

    this.reverseInterval = setInterval(() => {
      const currentTime = this.store.playableMedia.$currentTime.get();
      if (currentTime === undefined) {
        return;
      }
      if (!this.isMediaElementInitialized()) {
        return;
      }
      const newTime = currentTime - frameStep;
      this.mediaElement.currentTime = newTime;
      this.store.playableMedia.setCurrentTime(newTime);
    }, 1000 / fps); // The interval is set based on the frame rate
  };

  handleCurrentTime = (currentTime: number | undefined) => {
    if (currentTime === undefined) {
      return;
    }
    if (!this.isMediaElementInitialized()) {
      return;
    }

    if (this.isComparePlayer && !this.mediaElement.ended) {
      this.handleComparePlayerTime(currentTime);
      return;
    }
    this.handlePlayableRangeTime();
  };

  handlePlayableRangeTime = () => {
    if (!this.isMediaElementInitialized()) {
      return;
    }

    const playableRange = this.store.playableMedia.$playableRange.get();
    const currentSegmentRange = this.store.segment.$currentSegment?.get().range;

    const { start, end } = currentSegmentRange
      ? currentSegmentRange
      : playableRange;

    if (this.mediaElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
      const currentTime = this.store.playableMedia.$currentTime.get();

      if (currentTime !== undefined && currentTime < start) {
        this.store.setTargetTime(start);
      }
      if (currentTime !== undefined && currentTime > end) {
        this.store.playableMedia.pause();
        this.store.setTargetTime(end);
      }
    } else {
      this.mediaElement.addEventListener(
        'loadedmetadata',
        () => {
          const targetTime = this.store.playableMedia.$targetTime.get();
          if (this.mediaElement && targetTime !== undefined) {
            if (targetTime > start && targetTime < end) {
              this.seekToTargetTime(targetTime);
              return;
            }
            this.mediaElement.currentTime = start;
          }
        },
        { once: true },
      );
    }
  };

  handleComparePlayerTime = throttle((currentTime: number) => {
    if (!this.isMediaElementInitialized()) {
      return;
    }

    const frameDuration = this.store.playlist.$frameDuration.get();
    // Attempts to keep the desync between the players below a frame threshold.
    // 5 frames is an initial starting point which we will have to calibrate to find a good value.
    // Issues seem largest in Safari, calibration / changes should probably be made there.
    if (
      Math.abs(this.mediaElement.currentTime - currentTime) >
      frameDuration * 5
    ) {
      this.#hiddenRelease?.restore();
      if (this.store.playableMedia.$shouldBePlaying.get()) {
        this.mediaElement.play().catch(() => {});
      } else {
        this.mediaElement.pause();
      }
      this.mediaElement.currentTime = currentTime;
    }
  }, 1000);

  // If the media element is in an error state, reload it before trying to play it
  reloadIfMediaError = () => {
    if (!this.isMediaElementInitialized()) {
      return;
    }
    if (this.mediaElement.error && this.mediaElement.error.code > 1) {
      const currentTime = this.mediaElement.currentTime;
      this.mediaElement.load();
      this.mediaElement.currentTime = currentTime;
    }
  };

  async load(playlistItem: PlaylistItem): Promise<void> {
    if (!this.isMediaElementInitialized() || !playlistItem.url) {
      return;
    }
    const variant = getPreferredPlaylistItemVariant(
      playlistItem,
      this.store.playableMedia.$preferredVariants.get(),
    );
    this.mediaElement.src = variant?.url || playlistItem.url;
    this.mediaElement.load();
    if (this.isComparePlayer) {
      this.mediaElement.addEventListener(
        'loadedmetadata',
        () => {
          const targetTime = this.store.playableMedia.$targetTime.get();
          if (this.mediaElement && targetTime !== undefined) {
            this.seekToTargetTime(targetTime);
          }
        },
        { once: true },
      );
      return;
    }
  }

  replaceSource = (nextSource: string | undefined) => {
    if (!this.isMediaElementInitialized()) {
      return false;
    }
    this.#hiddenRelease?.restore();
    const currentSource = this.mediaElement.src;
    if (!currentSource || !nextSource || currentSource === nextSource) {
      return false;
    }

    logger.debug('Replacing source URL', currentSource, '->', nextSource);
    const currentTime = this.mediaElement.currentTime;
    this.mediaElement.src = nextSource;
    this.mediaElement.load();
    this.mediaElement.currentTime = currentTime;
  };

  play() {
    if (!this.isMediaElementInitialized()) {
      return;
    }
    this.#hiddenRelease?.restore();
    this.#createAudioNodes();
    if (this.store.playableMedia.$playbackRate.get() < 0) {
      this.playInReverse();
      return;
    }
    this.#audioNodes?.start();
    this.mediaElement.play().catch(() => {});
  }

  pause() {
    if (!this.isMediaElementInitialized()) {
      return;
    }
    this.mediaElement.pause();
  }

  // Firefox has a bug where the playback speed is not applied if it has connected audio nodes
  // when the video element doesn't have audio. Fortunately, Firefox has its own property to check
  // if the video element has audio data, but it only knows once the metadata is in.
  // Bugzilla report: https://bugzilla.mozilla.org/show_bug.cgi?id=1942609
  #createAudioNodes() {
    if (
      !this.isMediaElementInitialized() ||
      this.isComparePlayer ||
      this.#audioNodes !== undefined
    ) {
      return;
    }
    const mediaElement = this.mediaElement;
    if (!('mozHasAudio' in mediaElement)) {
      this.#audioNodes = new MediaPlayerAudioNodes(this.store, mediaElement);
      return;
    }
    if (mediaElement.readyState < HTMLMediaElement.HAVE_METADATA) {
      mediaElement.addEventListener(
        'loadedmetadata',
        this.#handleAudioNodesMetadata,
        { once: true },
      );
      return;
    }
    if (mediaElement.mozHasAudio) {
      this.#audioNodes = new MediaPlayerAudioNodes(this.store, mediaElement);
    }
  }

  #handleAudioNodesMetadata = () => {
    this.#createAudioNodes();
    if (this.store.playableMedia.$shouldBePlaying.get()) {
      this.#audioNodes?.start();
    }
  };

  refreshStoreCurrentTime(currentTime?: number) {
    const time = currentTime ?? this.mediaElement?.currentTime ?? 0;
    this.store.playableMedia.setCurrentTime(
      this.store.playableMedia.getFrameAlignedTime(time),
    );
  }
}
